import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  ELocalStorageKeys,
  GetAllPersonsByUserResponse,
  IAuthAccessToken,
  IAuthLoginPayload,
} from './entities/authentication.interface';
import { computed, inject, Injector } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, retry, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { Encrypt } from '@shared/utils';
import { LocalStorageFacade } from '@application/localstorage';
import { AuthService } from './infra/auth.service';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  APP_CONFIG,
  App_url_type_by_AppSource_enum,
  AppSource,
  AppUrlOptions,
} from '@application/config';
import { AlertStore } from '@alert/data-access';
import { WebAuthnService } from './infra/web-authn.service';
import { User } from './entities/user';
import { ServerMockService } from './infra/server-mock.service';
import { RouterStore } from '@route/data-access';
import { Capacitor } from '@capacitor/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';
import { LanguageStore } from '@settings/language/data-access';

export type IRememberEmail = {
  Email: string;
  PersonId: string;
};

export interface IAuthStore {
  authData: IAuthAccessToken | null;
  webauthn: any;
  tempLoginPayload: IAuthLoginPayload;
  emailToLogin: string;
  rememberNextLoginEmail: boolean;
  redirectToApp: AppUrlOptions;
  selectedRememberedAccount: any;
  allPersonsByUser: GetAllPersonsByUserResponse[];
  allAccountEmails: IRememberEmail[];
  allowUserInputCredentials: boolean;
  tempAuthData: IAuthAccessToken;
  temporaryAuthBackup: IAuthAccessToken | null;
  temporaryAuthExpiresAt: number | null;
  isTemporaryAuthActive: boolean;
}

export const initialAuthStore: IAuthStore = {
  authData: null,
  webauthn: null,
  tempLoginPayload: null,
  emailToLogin: null,
  rememberNextLoginEmail: false,
  redirectToApp: null,
  selectedRememberedAccount: null,
  allPersonsByUser: [],
  allAccountEmails: [],
  allowUserInputCredentials: true,
  tempAuthData: null,
  temporaryAuthBackup: null,
  temporaryAuthExpiresAt: null,
  isTemporaryAuthActive: false,
};

export const AuthStore = signalStore(
  {
    providedIn: 'root',
  },
  withState(initialAuthStore),
  withComputed((state, observe = toObservable(state.authData)) => ({
    getAuthData: computed(() => state.authData()),
    isLoggedIn: computed(() => !!state.authData()),
    getTempLoginPayload: computed(() => state.tempLoginPayload()),
    getEmailToLogin: computed(() => state.emailToLogin()),
    observableAuthData: computed(() => observe),
    isTemporaryAuthActive: computed(() => state.isTemporaryAuthActive()),
    temporaryAuthExpiresAt: computed(() => state.temporaryAuthExpiresAt()),
  })),
  withMethods(
    (
      state,
      injector = inject(Injector),
      crypt = inject(Encrypt),
      localStorage = inject(LocalStorageFacade),
      alertStore = inject(AlertStore),
      webAuthnService = inject(WebAuthnService),
      serverMockService = inject(ServerMockService),
      appConfig = inject(APP_CONFIG),
      routerStore = inject(RouterStore),
      errorStore = inject(ErrorStore),
      languageStore = inject(LanguageStore)
    ) => {
      const getAuthService = () => injector.get(AuthService);

      let temporaryAuthTimeout: ReturnType<typeof setTimeout> | null = null;

      const clearTemporaryAuthTimeout = () => {
        if (temporaryAuthTimeout) {
          clearTimeout(temporaryAuthTimeout);
          temporaryAuthTimeout = null;
        }
      };

      const restoreTemporaryAuth = () => {
        const backup = state.temporaryAuthBackup();
        clearTemporaryAuthTimeout();

        if (!backup) {
          patchState(state, {
            isTemporaryAuthActive: false,
            temporaryAuthBackup: null,
            temporaryAuthExpiresAt: null,
          });
          return;
        }

        patchState(state, {
          authData: backup,
          isTemporaryAuthActive: false,
          temporaryAuthBackup: null,
          temporaryAuthExpiresAt: null,
        });
      };

      const scheduleTemporaryAuthCleanup = (duration: number) => {
        clearTemporaryAuthTimeout();
        if (duration <= 0) {
          restoreTemporaryAuth();
          return;
        }

        temporaryAuthTimeout = setTimeout(() => {
          restoreTemporaryAuth();
        }, duration);
      };

      const getAppSourceEnvironment = (appSource: AppSource): AppUrlOptions => {
        return App_url_type_by_AppSource_enum[
          AppSource[appSource] as keyof typeof App_url_type_by_AppSource_enum
        ] as AppUrlOptions;
      };

      const logout = () => {
        clearTemporaryAuthTimeout();
        localStorage.removeItem(ELocalStorageKeys.auth);
        patchState(state, { authData: null });
        localStorage.removeItem(ELocalStorageKeys.webauthn);
        if (Capacitor.isNativePlatform()) {
          routerStore.navigate(['/onboarding']);
          return;
        }

        if (!location.href.includes('localhost')) {
          const appUrl = `${window.location.protocol}//accounts.${location.href
            .split('.')
            .slice(1)
            .join('.')}`;
          location.href =
            `${appUrl}` +
            `?next=${getAppSourceEnvironment(appConfig.appSource)}`;
        } else {
          location.href =
            appConfig.appUrl.accounts +
            `?next=${getAppSourceEnvironment(appConfig.appSource)}`;
        }
      };

      const transportBearerTo = (app: AppUrlOptions, data: any) => {
        if (!data) {
          return;
        }

        const goRedirect = () => {
          const base64 = window.btoa(JSON.stringify(data));
          if (!window.location.href.includes('localhost')) {
            let currentHostname = window.location.href
              .split('.')
              .slice(1)
              .join('.');

            if (currentHostname.split('/').length > 1) {
              currentHostname = currentHostname.split('/')[0];
            }
            let appUrl;
            if (currentHostname.includes('.dev')) {
              appUrl = `${window.location.protocol}//app.${currentHostname}`;
            } else {
              appUrl = `${window.location.protocol}//novo.${currentHostname}`;
            }

            window.location.href = `${appUrl}/transmission?token=${base64}`;
          } else {
            const appUrl = appConfig.appUrl.actuar_app;
            window.location.href = `${appUrl}/transmission?token=${base64}`;
          }
        };

        if (!app) {
          app = getAppSourceEnvironment(state.allPersonsByUser()[0].AppSource);
          goRedirect();
          return;
        }

        goRedirect();
      };

      const loadAllPersonsByUser = rxMethod<any>(
        pipe(
          switchMap((onComplete) =>
            getAuthService()
              .getAllPersonsByUser()
              .pipe(
                tap(() => {
                  patchState(state, {
                    ...setPropLoading('loadAllPersonsByUser'),
                  });
                }),
                tapResponse({
                  next: (result) => {
                    result.forEach(
                      (person) =>
                        (person.Email = state.tempLoginPayload().Email)
                    );
                    patchState(state, {
                      allPersonsByUser: result,
                      ...setPropLoaded('loadAllPersonsByUser'),
                    });
                    onComplete?.();
                  },
                  error: (e) => {
                    patchState(state, {
                      allPersonsByUser: [],
                      ...setPropError('loadAllPersonsByUser', e),
                    });
                  },
                })
              )
          )
        )
      );

      const addEmailAccountLocalStorage = (payload: IRememberEmail) => {
        const rememberEmail =
          JSON.parse(localStorage.getItem(ELocalStorageKeys.rememberEmail)) ||
          [];
        if (
          rememberEmail?.find((e: IRememberEmail) => e.Email === payload.Email)
        ) {
          return;
        }
        localStorage.setItem(
          ELocalStorageKeys.rememberEmail,
          JSON.stringify([...rememberEmail, payload])
        );
      };

      const removeEmailAccountLocalStorage = (email: string) => {
        const rememberEmail =
          JSON.parse(localStorage.getItem(ELocalStorageKeys.rememberEmail)) ||
          [];
        const newRememberEmail = rememberEmail.filter(
          (e: IRememberEmail) => e.Email !== email
        );
        localStorage.setItem(
          ELocalStorageKeys.rememberEmail,
          JSON.stringify(newRememberEmail)
        );
        patchState(state, { allAccountEmails: newRememberEmail });
      };

      const changePersonAccount = rxMethod<{
        PersonId: string;
        onComplete?: () => void;
      }>(
        pipe(
          tap(() => {
            patchState(state, { ...setPropLoading('changePersonAccount') });
          }),
          switchMap(({ PersonId, onComplete }) =>
            getAuthService()
              .changePersonAccount(PersonId)
              .pipe(
                tapResponse({
                  next: (authData) => {
                    localStorage.setItem(
                      ELocalStorageKeys.auth,
                      JSON.stringify(authData)
                    );
                    patchState(state, {
                      authData,
                      ...setPropLoaded('changePersonAccount'),
                    });
                    onComplete?.();
                  },
                  error: (e) => {
                    patchState(state, {
                      authData: null,
                      ...setPropError('doLogin', e),
                    });
                  },
                })
              )
          )
        )
      );

      const updateLanguageBySettings = rxMethod<void>(
        pipe(
          switchMap(() =>
            getAuthService()
              .getUserSettings()
              .pipe(
                tapResponse({
                  next: (userSettings) => {
                    languageStore.setLanguage(userSettings?.Language);
                  },
                  error: () => {},
                })
              )
          )
        )
      );

      const login = rxMethod<IAuthLoginPayload>(
        pipe(
          tap(() => {
            patchState(state, { ...setPropLoading('doLogin') });
          }),
          switchMap((loginForm) =>
            getAuthService()
              .login(loginForm)
              .pipe(
                tapResponse({
                  next: (authData) => {
                    patchState(state, {
                      authData,
                      tempAuthData: authData,
                      tempLoginPayload: loginForm,
                      ...setPropLoaded('doLogin'),
                    });

                    if (state.rememberNextLoginEmail()) {
                      const emailAccount = {
                        Email: loginForm.Email,
                        PersonId: authData.TenantId,
                      };
                      addEmailAccountLocalStorage(emailAccount);
                      if (
                        !state
                          .allAccountEmails()
                          .find(
                            (e: IRememberEmail) =>
                              e.Email === emailAccount.Email
                          )
                      ) {
                        patchState(state, {
                          allAccountEmails: [
                            ...state.allAccountEmails(),
                            emailAccount,
                          ],
                        });
                      }
                    }

                    const thenLoadAllPersonsByUser = () => {
                      if (state.allPersonsByUser().length === 1) {
                        transportBearerTo('actuar_app', state.authData());
                        return;
                      }
                      routerStore.navigate(['/auth/user-selection']);
                    };

                    loadAllPersonsByUser(thenLoadAllPersonsByUser);
                    updateLanguageBySettings();
                  },
                  error: (e: HttpErrorResponse) => {
                    patchState(state, {
                      authData: null,
                      ...setPropError('doLogin', e),
                    });

                    const passwordErrors =
                      e?.error?.Erros?.Password ?? e?.error?.Errors?.Password;
                    const isTemporaryPasswordError =
                      e.status === 400 &&
                      Array.isArray(passwordErrors) &&
                      passwordErrors.includes(
                        'Localize.UserService.Msg.TemporaryPassword'
                      );

                    if (isTemporaryPasswordError) {
                      patchState(state, { tempLoginPayload: { ...loginForm } });
                      routerStore.navigate(['/change-temporary-password']);
                    }
                  },
                })
              )
          )
        )
      );
      const loginWebAuthn = (user: User[]) => {
        webAuthnService
          .webAuthnSignin(user)
          .then(() => {
            const loginForm: IAuthLoginPayload = {
              Email: user[0].email,
              Password: user[0].password,
              Origin: window['isFacilFit']
                ? 'facilfit'
                : (user[0].origin as any),
            };
            return login(loginForm);
          })
          .catch((error) => {
            console.error('FAIL', error);
          });
      };
      const setAuthData = (data: IAuthAccessToken) => {
        patchState(state, { authData: data });
      };
      return {
        setAuthData,
        beginTemporaryAuthSession: (
          authData: IAuthAccessToken,
          durationMs = 30_000
        ) => {
          if (!authData?.Token) {
            return;
          }

          const backup = state.temporaryAuthBackup() ?? state.authData();
          patchState(state, {
            authData,
            temporaryAuthBackup: backup,
            isTemporaryAuthActive: true,
            temporaryAuthExpiresAt: Date.now() + durationMs,
          });

          scheduleTemporaryAuthCleanup(durationMs);
        },
        endTemporaryAuthSession: () => {
          restoreTemporaryAuth();
        },
        doLogin: (loginForm: IAuthLoginPayload) => {
          loginForm.Password = crypt.encryptMd5(
            loginForm.Email.toLowerCase() + loginForm.Password
          );
          if (window['isFacilFit']) {
            loginForm.Origin = 'facilfit';
          }
          return login(loginForm);
        },
        doRedirectToApp: () => {
          transportBearerTo('actuar_app', state.authData());
        },
        userHasWebAuthnCredentials: () => {
          return !!localStorage.getItem('credentials');
        },
        eraseWebauthnCredentials: () => {
          localStorage.removeItem('credentials');
        },
        selectAccountAndRedirect: (account: any) => {
          changePersonAccount({
            PersonId: account.PersonId,
            onComplete: () => {
              transportBearerTo('actuar_app', state.authData());
            },
          });
        },
        registerWebAuthn: () => {
          const loginForm = state.tempLoginPayload();
          const authData = state.authData();
          if (!loginForm || !authData) {
            return;
          }
          const user: User = {
            id: loginForm.Email,
            email: loginForm.Email,
            credentials: [],
            password: loginForm.Password,
          };
          webAuthnService
            .webAuthnSignup(user)
            .then((credential: PublicKeyCredential) => {
              const valid = serverMockService.registerCredential(
                user,
                credential
              );
              if (valid) {
                transportBearerTo('actuar_app', state.authData());
              }
            })
            .catch((error) => {
              alertStore.create({
                message: 'LOGIN.BIOMETRICS.FAILED_TO_CREATE_BIOMETRY',
                title: 'LOGIN.BIOMETRICS.ALERT_TITLE_CREATE_BIOMETRY',
                type: 'warning',
              });
            });
        },
        loginWebAuthn: () => {
          const user = JSON.parse(localStorage.getItem('credentials'));
          loginWebAuthn(user);
        },
        setEmailLogin(emailToLogin: string) {
          patchState(state, { emailToLogin });
        },
        setRemeberedAccountToLogin(account: any) {
          patchState(state, { selectedRememberedAccount: account });
        },
        eraseLocalAllPersonsByUser() {
          localStorage.removeItem('allPersonsByUser');
          patchState(state, { allPersonsByUser: [] });
        },
        removeOneRememberedAccount(email: string) {
          removeEmailAccountLocalStorage(email);
        },
        setAllowUserInputCredentials(allow: boolean) {
          patchState(state, { allowUserInputCredentials: allow });
        },
        setTempLoginPayload(tempLoginPayload: IAuthLoginPayload) {
          patchState(state, { tempLoginPayload });
        },
        preferPassword: (password: boolean) => {
          localStorage.setItem('password', password);
        },
        setRedirectToApp(redirectToApp: AppUrlOptions) {
          patchState(state, { redirectToApp: redirectToApp });
        },
        rememberLoginEmail(rememberNextLoginEmail: boolean) {
          patchState(state, { rememberNextLoginEmail });
        },
        loginWithTempData: () => login(state.tempLoginPayload()),
        refreshToken: rxMethod<void>(
          pipe(
            switchMap(() =>
              getAuthService()
                .refreshToken(state.authData().TokenId)
                .pipe(
                  retry(2),
                  tapResponse({
                    next: (authData) => {
                      patchState(state, {
                        authData,
                        ...setPropLoaded('refreshToken'),
                      });
                      console.log('refreshToken', authData);
                      localStorage.setItem(
                        ELocalStorageKeys.auth,
                        JSON.stringify(authData)
                      );
                      if (authData.TokenId.includes('0000')) {
                        logout();
                      }
                    },
                    error: () => logout(),
                  })
                )
            )
          )
        ),
        loginByCodeTemp: rxMethod<{ sid: string; uid: string }>(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('loginByCodeTemp') })
            ),
            switchMap(({ sid, uid }) => {
              return getAuthService()
                .loginByCodeTemp({ sid, uid })
                .pipe(
                  tapResponse({
                    next: (authData) => {
                      setAuthData(authData);
                      patchState(state, { tempAuthData: authData });

                      getAuthService()
                        .getAllPersonsByUser()
                        .pipe(
                          tapResponse({
                            next: (result) => {
                              patchState(state, { allPersonsByUser: result });

                              if (result.length === 1 || !result?.length) {
                                transportBearerTo('actuar_app', authData);
                              } else {
                                transportBearerTo('actuar_app', authData);
                              }
                            },
                            error: (e) => {
                              console.error(e);
                              transportBearerTo('actuar_app', authData);
                            },
                          })
                        )
                        .subscribe();

                      patchState(state, {
                        ...setPropLoaded('loginByCodeTemp'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(state, {
                        ...setPropError('loginByCodeTemp', error),
                      });
                    },
                  })
                );
            })
          )
        ),
        changePersonAccount: changePersonAccount,
        logout() {
          logout();
        },
        resetReqState: () => {
          patchState(state, {
            ...setPropInit(
              'doLogin',
              'refreshToken',
              'loginWithTempData',
              'changePersonAccount',
              'loadAllPersonsByUser'
            ),
          });
        },
      };
    }
  ),
  withHooks({
    onInit: (state) => {
      const localStorage = inject(LocalStorageFacade);
      const authData = JSON.parse(localStorage.getItem(ELocalStorageKeys.auth));
      const webauthn = JSON.parse(
        localStorage.getItem(ELocalStorageKeys.webauthn)
      );
      const allPersonsByUser =
        JSON.parse(localStorage.getItem('allPersonsByUser')) || [];
      const rememberEmail =
        JSON.parse(localStorage.getItem(ELocalStorageKeys.rememberEmail)) || [];
      if (authData) {
        patchState(state, { authData });
      }
      if (webauthn) {
        patchState(state, { webauthn, emailToLogin: webauthn[0].email });
      }
      if (allPersonsByUser) {
        patchState(state, { allPersonsByUser });
      }
      if (rememberEmail) {
        patchState(state, {
          allAccountEmails: rememberEmail,
          allowUserInputCredentials: false,
        });
      }
    },
  }),
  withReqState(
    'doLogin',
    'refreshToken',
    'loginWithTempData',
    'changePersonAccount',
    'loadAllPersonsByUser',
    'loginByCodeTemp'
  )
);
